import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { ChevronLeft, ChevronRight, ArrowLeft, X, ExternalLink, Wine, Leaf, Snowflake, ChefHat, Beaker, Search, ChevronDown, Grape, UtensilsCrossed, Zap, ClipboardList, Eye, EyeOff, GripVertical, ChevronUp, AlertTriangle, ChevronsUpDown, Check } from "lucide-react";
import { CardTestModal } from "../components/CardTestModal";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* ─── Color constants ─── */
const C = {
  cardBg: "#111111",
  banner: "#161616",
  headerBg: "#0d0d0d",
  copper: "#dcaa2c",
  copperLight: "rgba(220,170,44,0.2)",
  textMuted: "#888",
  textLight: "#bbb",
  pageBg: "#000",
  gridLine: "#222",
};

/* ─── Mastery banner: binary review-scheduling status (Phase 2). Green
   ("Mastered") while now < review_due_at from the most recent 100% attempt;
   red ("Due to Review") once that fixed interval has passed, or if the card
   was never mastered. No gradient — a hard flip on the scheduled date. ─── */
const MASTERY_RED = "rgb(185, 28, 28)";
/* Phase 7: darkened from the lighter rgb(34,197,94) to rgb(21,128,61) —
   the lighter green only reached ~2.3:1 contrast with the white label text
   (fails WCAG AA's 4.5:1 for this small bold text); this shade reaches
   ~5:1 while staying clearly "green" and matching the red banner's own
   similarly deep, muted tone. */
const MASTERY_GREEN = "rgb(21, 128, 61)";
const getMasteryBannerColor = (mastery) => (mastery?.mastered ? MASTERY_GREEN : MASTERY_RED);
const getMasteryBannerLabel = (mastery) => (mastery?.mastered ? "Mastered" : "Due to Review");
const daysUntil = (isoDate) => {
  if (!isoDate) return null;
  const diffMs = new Date(isoDate).getTime() - Date.now();
  return Math.max(0, Math.ceil(diffMs / 86400000));
};

/* Mirrors backend `questions.build_full_recipe_ingredients` exactly — swaps the
   consolidated batch line for its original per-spirit breakdown. Reused here
   (not re-derived/guessed) so Study Card "Full Recipe & Method" never shows
   data that disagrees with the Full Recipe test/practice question set. */
const buildFullRecipeIngredients = (cocktail) => {
  const batchName = (cocktail.batch_ingredient_name || "").trim().toLowerCase();
  if (!batchName) return cocktail.ingredients || [];
  const nonBatch = (cocktail.ingredients || []).filter(i => (i.name || "").trim().toLowerCase() !== batchName);
  return [...nonBatch, ...(cocktail.batch_breakdown || [])];
};

/* ─── Info Popup ─── */
function InfoPopup({ item, onClose }) {
  if (!item) return null;
  const hasVideo = item.youtube_link?.includes("youtu");
  const videoId = hasVideo ? item.youtube_link.match(/(?:v=|\.be\/)([\w-]+)/)?.[1] : null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose} data-testid="info-popup">
      <button onClick={onClose} className="absolute top-4 right-4 z-10 rounded-full p-2" style={{ background: C.copper }} data-testid="info-popup-close" aria-label="Close">
        <X size={20} className="text-white" />
      </button>
      <div className="relative max-w-2xl w-full max-h-[85vh] overflow-y-auto rounded-xl border-2 overflow-hidden" style={{ borderColor: C.copper, background: C.cardBg }} onClick={e => e.stopPropagation()}>
        {item.image_url && (
          <div className="w-full aspect-[4/3] bg-white/10">
            <img src={`${BACKEND}${item.image_url}`} alt={item.name} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="p-5 space-y-3">
          <h3 className="text-xl font-bold text-white" style={{ fontFamily: "Georgia, serif" }}>{item.name}</h3>
          {item.description && <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: C.textLight }}>{item.description}</p>}
          {videoId && (
            <div className="aspect-video rounded-lg overflow-hidden bg-black mt-2">
              <iframe src={`https://www.youtube.com/embed/${videoId}`} title={item.name} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          )}
          {hasVideo && !videoId && (
            <a href={item.youtube_link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm hover:underline" style={{ color: C.copper }}>
              <ExternalLink size={14} /> Watch Video
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   VIEW 1: DECKS — Category browser with expandable decks
   ═══════════════════════════════════════════════════════════ */
function DecksView({ cocktails, library, customDecks = [], onSelectCocktail, onSelectDeckCocktail, onChallenge, onTest, onTestCard, cardMastery = {}, onBack, isAdmin = false, hiddenCategories = [], onToggleHideCategory, deckOrder = [], onReorderDecks, recipeMode, onRecipeModeChange, ingredientNames = [] }) {
  const [search, setSearch] = useState("");
  const [manualExpanded, setManualExpanded] = useState(null);
  const [dragCategory, setDragCategory] = useState(null);
  const [filterIngredients, setFilterIngredients] = useState([]);
  const [ingredientPickerOpen, setIngredientPickerOpen] = useState(false);
  const setRecipeMode = onRecipeModeChange; // controlled by the parent so the choice survives a Back-to-Deck round trip

  const isSearching = search.trim().length > 0 || filterIngredients.length > 0;
  const q = search.toLowerCase();
  const matchesFilters = (c) => {
    if (search.trim() && !c.name.toLowerCase().includes(q)) return false;
    if (filterIngredients.length > 0 && !([...(c.ingredients || []), ...(c.batch_breakdown || [])]).some(ing => filterIngredients.includes(ing.name))) return false;
    return true;
  };

  const findLibItem = (type, name) => {
    if (!name) return null;
    return library.find(l => l.type === type && l.name.toLowerCase() === name.toLowerCase());
  };

  const hiddenSet = useMemo(() => new Set(hiddenCategories), [hiddenCategories]);

  // Build cocktail map for custom deck lookups
  const cocktailMap = Object.fromEntries(cocktails.map(c => [c.id, c]));

  // Group cocktails by category, apply org's saved deck order (falling back to
  // alphabetical for anything the admin hasn't explicitly sorted).
  const categoryDecks = useMemo(() => {
    const groups = {};
    cocktails.forEach(c => {
      const cat = c.category || "Uncategorized";
      if (!groups[cat]) groups[cat] = { all: [], filtered: [] };
      groups[cat].all.push(c);
      if (!isSearching || matchesFilters(c)) {
        groups[cat].filtered.push(c);
      }
    });
    const orderIndex = new Map(deckOrder.map((name, idx) => [name, idx]));
    const legacyOrder = ["Signature", "Classic", "Non alcoholic"];
    return Object.entries(groups).sort((a, b) => {
      const ai = orderIndex.has(a[0]) ? orderIndex.get(a[0]) : Infinity;
      const bi = orderIndex.has(b[0]) ? orderIndex.get(b[0]) : Infinity;
      if (ai !== bi) return ai - bi;
      const la = legacyOrder.indexOf(a[0]);
      const lb = legacyOrder.indexOf(b[0]);
      if (la >= 0 && lb >= 0) return la - lb;
      if (la >= 0) return -1;
      if (lb >= 0) return 1;
      return a[0].localeCompare(b[0]);
    });
  }, [cocktails, isSearching, q, filterIngredients, deckOrder]);

  // Admins see all categories; for staff, hide categories in hiddenSet entirely
  // (defence in depth — backend already filters them out, but if a hidden category
  // ever slipped through, this keeps it out of the UI).
  const visibleCategoryDecks = isAdmin
    ? categoryDecks.filter(([cat]) => !hiddenSet.has(cat))
    : categoryDecks.filter(([cat]) => !hiddenSet.has(cat));
  const hiddenCategoryDecks = isAdmin ? categoryDecks.filter(([cat]) => hiddenSet.has(cat)) : [];

  // Custom decks with resolved cocktails
  const resolvedCustomDecks = useMemo(() => {
    return customDecks.map(deck => {
      const all = (deck.cocktail_ids || []).map(id => cocktailMap[id]).filter(Boolean);
      const filtered = isSearching ? all.filter(matchesFilters) : all;
      return { ...deck, all, filtered };
    }).filter(d => d.all.length > 0);
  }, [customDecks, cocktailMap, isSearching, q, filterIngredients]);

  const totalMatches = isSearching
    ? categoryDecks.reduce((s, [, d]) => s + d.filtered.length, 0) + resolvedCustomDecks.reduce((s, d) => s + d.filtered.length, 0)
    : 0;

  // Reorder helper: computes a full order (visible + hidden categories) with
  // `category` moved by `direction` (+1 or -1) among the visible-only slice.
  const moveCategory = (category, direction) => {
    if (!onReorderDecks) return;
    const visibleNames = visibleCategoryDecks.map(([n]) => n);
    const idx = visibleNames.indexOf(category);
    const target = idx + direction;
    if (idx < 0 || target < 0 || target >= visibleNames.length) return;
    const next = [...visibleNames];
    [next[idx], next[target]] = [next[target], next[idx]];
    const hiddenNames = hiddenCategoryDecks.map(([n]) => n);
    onReorderDecks([...next, ...hiddenNames]);
  };

  const dropOnCategory = (target) => {
    if (!onReorderDecks || !dragCategory || dragCategory === target) return;
    const visibleNames = visibleCategoryDecks.map(([n]) => n);
    const from = visibleNames.indexOf(dragCategory);
    const to = visibleNames.indexOf(target);
    if (from < 0 || to < 0) return;
    const next = [...visibleNames];
    next.splice(from, 1);
    next.splice(to, 0, dragCategory);
    const hiddenNames = hiddenCategoryDecks.map(([n]) => n);
    onReorderDecks([...next, ...hiddenNames]);
  };

  return (
    <div className="max-w-2xl mx-auto rounded-xl border overflow-hidden shadow-2xl" style={{ borderColor: C.copper, background: C.cardBg }} data-testid="decks-view">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2" style={{ background: C.headerBg }}>
        <span className="text-white font-bold tracking-wide text-sm" style={{ fontFamily: "Georgia, serif" }}>
          FlashSpecs <span style={{ color: C.copper }}>|</span> <span className="font-normal" style={{ color: C.textMuted }}>Cocktails</span>
        </span>
        {onBack && (
          <button onClick={onBack} className="rounded-full p-1 hover:bg-white/10 transition-colors" aria-label="Back">
            <X size={18} style={{ color: C.copper }} />
          </button>
        )}
      </div>

      {/* Search */}
      <div className="px-2 py-1.5 border-b space-y-1.5" style={{ borderColor: C.copperLight }}>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-shadow focus-within:ring-2 focus-within:ring-[hsl(var(--primary))]" style={{ borderColor: C.copperLight, background: "rgba(255,255,255,0.05)" }}>
          <Search size={16} style={{ color: C.textMuted }} />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search Cocktail Cards"
            className="bg-transparent border-none outline-none text-sm flex-1 text-white placeholder:text-[#555]"
            data-testid="flashspec-search"
          />
        </div>

        {/* Ingredient filter — e.g. practise just the cocktails that use "Lime Juice" */}
        {ingredientNames.length > 0 && (
          <Popover open={ingredientPickerOpen} onOpenChange={setIngredientPickerOpen}>
            <PopoverTrigger asChild>
              <button
                role="combobox"
                aria-expanded={ingredientPickerOpen}
                className="w-full flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg border text-sm transition-colors"
                style={{ borderColor: C.copperLight, background: "rgba(255,255,255,0.05)", color: filterIngredients.length ? "#fff" : C.textMuted }}
                data-testid="flashspec-ingredient-filter-trigger"
              >
                <span className="truncate text-left">
                  {filterIngredients.length > 0 ? `${filterIngredients.length} ingredient${filterIngredients.length > 1 ? 's' : ''} selected` : "Practise a specific ingredient..."}
                </span>
                <ChevronsUpDown size={14} className="shrink-0 opacity-50" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-72 p-0 bg-[hsl(var(--popover))] border-[hsl(var(--border))]" align="start">
              <Command>
                <CommandInput placeholder="Type an ingredient..." data-testid="flashspec-ingredient-filter-search" />
                <CommandList>
                  <CommandEmpty>No ingredient found.</CommandEmpty>
                  <CommandGroup>
                    {ingredientNames.map((name) => (
                      <CommandItem
                        key={name}
                        value={name}
                        onSelect={() => {
                          setFilterIngredients(prev =>
                            prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
                          );
                        }}
                        data-testid={`flashspec-ingredient-filter-option-${name}`}
                      >
                        <Check size={14} className={`mr-2 ${filterIngredients.includes(name) ? 'opacity-100' : 'opacity-0'}`} />
                        {name}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        )}
        {filterIngredients.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {filterIngredients.map((name) => (
              <div
                key={name}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium"
                style={{ background: C.copper, color: "#000" }}
                data-testid={`flashspec-ingredient-filter-pill-${name}`}
              >
                {name}
                <button onClick={() => setFilterIngredients(prev => prev.filter(n => n !== name))} aria-label={`Remove ${name} filter`} data-testid={`flashspec-ingredient-filter-remove-${name}`}>
                  <X size={11} />
                </button>
              </div>
            ))}
            <button
              onClick={() => setFilterIngredients([])}
              className="text-xs underline"
              style={{ color: C.textMuted }}
              data-testid="flashspec-ingredient-filter-clear"
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Recipe mode: which question set Practise/Test should use for batched cocktails */}
      <div className="px-3 py-2 border-b flex items-center justify-center gap-1.5 sm:gap-2 overflow-x-auto" style={{ borderColor: C.copperLight }}>
        <button
          onClick={() => setRecipeMode("batch")}
          className="px-2.5 sm:px-3 py-1 rounded-full text-[9px] sm:text-[10px] font-bold uppercase tracking-wide whitespace-nowrap transition-all"
          style={recipeMode === "batch" ? { background: C.copper, color: "#000" } : { color: C.textMuted, border: "1px solid #333" }}
          data-testid="recipe-mode-batch"
        >
          Batch Measurements
        </button>
        <button
          onClick={() => setRecipeMode("full")}
          className="px-2.5 sm:px-3 py-1 rounded-full text-[9px] sm:text-[10px] font-bold uppercase tracking-wide whitespace-nowrap transition-all"
          style={recipeMode === "full" ? { background: C.copper, color: "#000" } : { color: C.textMuted, border: "1px solid #333" }}
          data-testid="recipe-mode-full"
        >
          Full Recipe & Method
        </button>
      </div>

      {/* Deck List */}
      <div className="p-2 space-y-2 max-h-[70vh] overflow-y-auto" data-testid="deck-list">
        {isSearching && totalMatches === 0 && (
          <p className="text-center text-xs py-6" style={{ color: C.textMuted }}>
            {search.trim() ? <>No cocktails match &ldquo;{search}&rdquo;</> : "No cocktails found with the selected ingredient(s)"}
          </p>
        )}

        {/* Custom Decks */}
        {resolvedCustomDecks.map(deck => {
          if (isSearching && deck.filtered.length === 0) return null;
          const key = `custom-${deck.id}`;
          const isExpanded = isSearching ? deck.filtered.length > 0 : manualExpanded === key;
          const displayItems = isSearching ? deck.filtered : deck.all;

          return (
            <div key={key}>
              <button
                onClick={() => { if (!isSearching) setManualExpanded(isExpanded ? null : key); }}
                className="w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all"
                style={{ background: C.banner }}
                data-testid={`deck-custom-${deck.id}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-white font-bold text-sm uppercase tracking-wide" style={{ fontFamily: "Georgia, serif" }}>
                    {deck.name}
                  </span>
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: "rgba(184,151,106,0.2)", color: C.copper }}>
                    {isSearching ? `${deck.filtered.length}/${deck.all.length}` : deck.all.length}
                  </span>
                </div>
                <ChevronDown
                  size={16}
                  className="transition-transform"
                  style={{ color: C.textMuted, transform: isExpanded ? "rotate(180deg)" : "none" }}
                />
              </button>
              {isExpanded && (
                <>
                  <div className="mt-2 grid grid-cols-3 gap-1.5" data-testid={`deck-items-custom-${deck.id}`}>
                    {displayItems.map(cocktail => (
                      <div
                        key={cocktail.id}
                        onClick={() => onSelectDeckCocktail(cocktail, deck.all, recipeMode)}
                        className="relative rounded-lg border overflow-hidden transition-all hover:brightness-110 cursor-pointer"
                        style={{ borderColor: C.copperLight, background: "rgba(255,255,255,0.03)" }}
                        data-testid={`cocktail-item-${cocktail.id}`}
                      >
                        {onTestCard && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onTestCard(cocktail); }}
                            className="absolute top-0 left-0 right-0 z-10 h-5 flex items-center justify-center transition-colors"
                            style={{ background: getMasteryBannerColor(cardMastery[cocktail.id]) }}
                            title="Practise this card"
                            data-testid={`test-card-btn-${cocktail.id}`}
                          >
                            <span className="text-[10px] font-bold uppercase tracking-wider text-white">{getMasteryBannerLabel(cardMastery[cocktail.id])}</span>
                          </button>
                        )}
                        <div className="aspect-square flex items-center justify-center overflow-hidden" style={{ background: cocktail.image_url ? "transparent" : "rgba(255,255,255,0.04)" }}>
                          {cocktail.image_url ? (
                            <img src={`${BACKEND}${cocktail.image_url}`} alt={cocktail.name} className="w-full h-full object-cover" />
                          ) : (
                            <Wine size={24} style={{ color: "#333" }} />
                          )}
                        </div>
                        <div className="px-1.5 py-2">
                          <p className="text-[10px] font-bold text-white uppercase tracking-wide text-center leading-tight" style={{ fontFamily: "Georgia, serif" }}>
                            {cocktail.name}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-2 px-1">
                    <button
                      onClick={() => onChallenge(deck.all.map(c => c.id), deck.name, recipeMode)}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wide transition-all hover:brightness-110"
                      style={{ background: C.copper, color: "#000" }}
                      data-testid={`challenge-btn-custom-${deck.id}`}
                    >
                      <Zap size={14} /> Practise
                    </button>
                    <button
                      onClick={() => onTest(deck.all.map(c => c.id), deck.name, recipeMode)}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wide border transition-all hover:bg-white/5"
                      style={{ borderColor: C.copper, color: C.copper }}
                      data-testid={`test-btn-custom-${deck.id}`}
                    >
                      <ClipboardList size={14} /> Test
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}

        {/* Category Decks — visible (or all, for staff). Admin sees hidden ones greyed at the bottom. */}
        {visibleCategoryDecks.map(([category, { all, filtered }], catIdx) => {
          if (isSearching && filtered.length === 0) return null;
          const isExpanded = isSearching ? filtered.length > 0 : manualExpanded === category;
          const displayItems = isSearching ? filtered : all;
          const canReorder = isAdmin && !isSearching && onReorderDecks;

          return (
            <div
              key={category}
              draggable={canReorder || undefined}
              onDragStart={canReorder ? () => setDragCategory(category) : undefined}
              onDragOver={canReorder ? (e) => { e.preventDefault(); } : undefined}
              onDrop={canReorder ? (e) => { e.preventDefault(); dropOnCategory(category); setDragCategory(null); } : undefined}
              onDragEnd={canReorder ? () => setDragCategory(null) : undefined}
              className={dragCategory === category ? "opacity-40" : ""}
            >
              <div className="flex items-center gap-2">
                {canReorder && (
                  <div className="flex flex-col items-center gap-0.5 shrink-0" data-testid={`reorder-${category.toLowerCase().replace(/\s/g, '-')}`}>
                    <button
                      onClick={(e) => { e.stopPropagation(); moveCategory(category, -1); }}
                      disabled={catIdx === 0}
                      className="p-1 rounded transition-all hover:brightness-125 disabled:opacity-30 disabled:cursor-not-allowed"
                      style={{ background: C.banner, color: C.copper }}
                      title="Move up"
                      aria-label="Move deck up"
                      data-testid={`move-up-${category.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <ChevronUp size={12} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); moveCategory(category, 1); }}
                      disabled={catIdx === visibleCategoryDecks.length - 1}
                      className="p-1 rounded transition-all hover:brightness-125 disabled:opacity-30 disabled:cursor-not-allowed"
                      style={{ background: C.banner, color: C.copper }}
                      title="Move down"
                      aria-label="Move deck down"
                      data-testid={`move-down-${category.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <ChevronDown size={12} />
                    </button>
                  </div>
                )}
                {canReorder && (
                  <div
                    className="p-2 rounded-lg cursor-grab active:cursor-grabbing hidden md:flex items-center"
                    style={{ background: C.banner, color: C.textMuted }}
                    title="Drag to reorder"
                    data-testid={`drag-handle-${category.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <GripVertical size={14} />
                  </div>
                )}
                <button
                  onClick={() => { if (!isSearching) setManualExpanded(isExpanded ? null : category); }}
                  className="flex-1 flex items-center justify-between px-4 py-3 rounded-lg transition-all"
                  style={{ background: C.banner }}
                  data-testid={`deck-${category.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-white font-bold text-sm uppercase tracking-wide" style={{ fontFamily: "Georgia, serif" }}>
                      {category}
                    </span>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: "rgba(184,151,106,0.2)", color: C.copper }}>
                      {isSearching ? `${filtered.length}/${all.length}` : all.length}
                    </span>
                  </div>
                  <ChevronDown
                    size={16}
                    className="transition-transform"
                    style={{ color: C.textMuted, transform: isExpanded ? "rotate(180deg)" : "none" }}
                  />
                </button>
                {isAdmin && (
                  <button
                    onClick={(e) => { e.stopPropagation(); onToggleHideCategory?.(category); }}
                    className="p-2 rounded-lg transition-all hover:brightness-125"
                    style={{ background: C.banner, color: C.textMuted }}
                    title="Hide this category from staff"
                    data-testid={`hide-category-${category.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <Eye size={16} />
                  </button>
                )}
              </div>
              {isExpanded && (
                <>
                  <div className="mt-2 grid grid-cols-3 gap-1.5" data-testid={`deck-items-${category.toLowerCase().replace(/\s/g, '-')}`}>
                    {displayItems.map(cocktail => (
                      <div
                        key={cocktail.id}
                        onClick={() => onSelectCocktail(cocktail, recipeMode)}
                        className="relative rounded-lg border overflow-hidden transition-all hover:brightness-110 cursor-pointer"
                        style={{ borderColor: C.copperLight, background: "rgba(255,255,255,0.03)" }}
                        data-testid={`cocktail-item-${cocktail.id}`}
                      >
                        {onTestCard && (
                          <button
                            onClick={(e) => { e.stopPropagation(); onTestCard(cocktail); }}
                            className="absolute top-0 left-0 right-0 z-10 h-5 flex items-center justify-center transition-colors"
                            style={{ background: getMasteryBannerColor(cardMastery[cocktail.id]) }}
                            title="Practise this card"
                            data-testid={`test-card-btn-${cocktail.id}`}
                          >
                            <span className="text-[10px] font-bold uppercase tracking-wider text-white">{getMasteryBannerLabel(cardMastery[cocktail.id])}</span>
                          </button>
                        )}
                        <div className="aspect-square flex items-center justify-center overflow-hidden" style={{ background: cocktail.image_url ? "transparent" : "rgba(255,255,255,0.04)" }}>
                          {cocktail.image_url ? (
                            <img src={`${BACKEND}${cocktail.image_url}`} alt={cocktail.name} className="w-full h-full object-cover" />
                          ) : (
                            <Wine size={24} style={{ color: "#333" }} />
                          )}
                        </div>
                        <div className="px-1.5 py-2">
                          <p className="text-[10px] font-bold text-white uppercase tracking-wide text-center leading-tight" style={{ fontFamily: "Georgia, serif" }}>
                            {cocktail.name}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2 mt-2 px-1">
                    <button
                      onClick={() => onChallenge(all.map(c => c.id), category, recipeMode)}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wide transition-all hover:brightness-110"
                      style={{ background: C.copper, color: "#000" }}
                      data-testid={`challenge-btn-${category.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <Zap size={14} /> Practise
                    </button>
                    <button
                      onClick={() => onTest(all.map(c => c.id), category, recipeMode)}
                      className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-xs font-bold uppercase tracking-wide border transition-all hover:bg-white/5"
                      style={{ borderColor: C.copper, color: C.copper }}
                      data-testid={`test-btn-${category.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <ClipboardList size={14} /> Test
                    </button>
                  </div>
                </>
              )}
            </div>
          );
        })}

        {/* Hidden Categories — admin only, greyed and unhideable in one tap */}
        {isAdmin && hiddenCategoryDecks.length > 0 && !isSearching && (
          <div className="pt-3 mt-3 border-t" style={{ borderColor: C.copperLight }}>
            <p className="text-[10px] uppercase tracking-widest font-bold mb-2 px-1" style={{ color: C.textMuted }}>
              Hidden from staff
            </p>
            {hiddenCategoryDecks.map(([category, { all }]) => (
              <div key={`hidden-${category}`} className="flex items-center gap-2 mb-2 opacity-60">
                <div
                  className="flex-1 flex items-center justify-between px-4 py-3 rounded-lg"
                  style={{ background: C.banner }}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-white font-bold text-sm uppercase tracking-wide line-through" style={{ fontFamily: "Georgia, serif" }}>
                      {category}
                    </span>
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: "rgba(184,151,106,0.2)", color: C.copper }}>
                      {all.length}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => onToggleHideCategory?.(category)}
                  className="p-2 rounded-lg transition-all hover:brightness-125 opacity-100"
                  style={{ background: C.banner, color: C.copper }}
                  title="Unhide this category"
                  data-testid={`unhide-category-${category.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <EyeOff size={16} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-4 py-2 flex justify-end" style={{ background: C.headerBg }}>
        <button onClick={onBack} className="rounded-full p-1.5" style={{ color: C.copper }} data-testid="decks-close" aria-label="Close deck list">
          <X size={20} />
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   VIEW 2: CARD — Individual cocktail FlashSpec detail
   ═══════════════════════════════════════════════════════════ */
function CardView({ cocktails, library, startIndex, onBack, testName, onTestCard, cardMastery = {}, initialRecipeMode = "batch" }) {
  const [currentIndex, setCurrentIndex] = useState(startIndex || 0);
  const [popup, setPopup] = useState(null);
  const [recipeMode, setRecipeMode] = useState(initialRecipeMode);
  const [hideRecipe, setHideRecipe] = useState(false);
  const [revealed, setRevealed] = useState(true);

  const findLibItem = (type, name) => {
    if (!name) return null;
    return library.find(l => l.type === type && l.name.toLowerCase() === name.toLowerCase());
  };

  const openPopup = (type, name) => {
    const item = findLibItem(type, name);
    setPopup(item || { name: name || "Unknown", description: `No library entry for "${name}" yet.`, image_url: null, youtube_link: null });
  };

  // Active recall: jumping to a different card always starts hidden again
  // while Recall Mode is on, so flipping through a deck keeps testing recall
  // rather than staying revealed from a previous card. Setting `revealed` in
  // the same event handler as `setCurrentIndex` (instead of a separate
  // useEffect that fires after the new card has already painted) means React
  // batches both updates into one commit — the new card's recipe never
  // reaches the DOM even for a frame.
  const goToCard = (idx) => {
    setCurrentIndex(idx);
    setRevealed(!hideRecipe);
  };
  const prev = () => goToCard(Math.max(0, currentIndex - 1));
  const next = () => goToCard(Math.min(cocktails.length - 1, currentIndex + 1));

  const cocktail = cocktails[currentIndex];
  if (!cocktail) return null;

  const hasBatch = !!cocktail.batch_ingredient_name;
  const batchDataIncomplete = hasBatch && recipeMode === "full" && !(cocktail.batch_breakdown || []).length;
  const displayIngredients = recipeMode === "full" ? buildFullRecipeIngredients(cocktail) : (cocktail.ingredients || []);
  const batchNameLower = (cocktail.batch_ingredient_name || "").trim().toLowerCase();

  const badges = [
    { icon: Wine, label: cocktail.glassware || "Glass", item: findLibItem("glassware", cocktail.glassware), type: "glassware", name: cocktail.glassware },
    { icon: Leaf, label: cocktail.garnish || "Garnish", item: findLibItem("garnish", cocktail.garnish), type: "garnish", name: cocktail.garnish },
    { icon: ChefHat, label: cocktail.method || "Method", item: findLibItem("method", cocktail.method), type: "method", name: cocktail.method },
    { icon: Snowflake, label: cocktail.ice || "Ice", item: findLibItem("ice", cocktail.ice), type: "ice", name: cocktail.ice },
  ];

  return (
    <>
      <div className="max-w-4xl space-y-3 -mx-7 sm:mx-auto">
        {/* Top bar — Back-to-deck + quick prev/next so long cards never strand
            navigation below the fold on short mobile screens */}
        <div className="flex items-center justify-between px-2 sm:px-0">
          <button onClick={onBack} className="flex items-center gap-1 text-sm font-medium" style={{ color: C.textMuted }} data-testid="flashspec-back">
            <ArrowLeft size={16} /> Back to Deck
          </button>
          <div className="flex items-center gap-2">
            <button onClick={prev} disabled={currentIndex === 0} aria-label="Previous card" className="p-2.5 -m-1.5 rounded disabled:opacity-20 transition-opacity" style={{ color: C.copper }} data-testid="flashspec-prev-top">
              <ChevronLeft size={16} />
            </button>
            <span className="text-xs whitespace-nowrap" style={{ color: C.textMuted }} data-testid="flashspec-position">
              Card {currentIndex + 1} of {cocktails.length}{testName ? ` — ${testName}` : ""}
            </span>
            <button onClick={next} disabled={currentIndex === cocktails.length - 1} aria-label="Next card" className="p-2.5 -m-1.5 rounded disabled:opacity-20 transition-opacity" style={{ color: C.copper }} data-testid="flashspec-next-top">
              <ChevronRight size={16} />
            </button>
          </div>
        </div>

        {/* Card */}
        <div className="rounded-xl overflow-hidden border shadow-2xl" style={{ borderColor: "#333", background: C.cardBg }} data-testid={`flashspec-${currentIndex}`}>
          {/* Practice Card banner — red ("Due to Review") until a 100% attempt starts a
              fixed review interval, green ("Mastered") until that date, then back to red.
              Purely informational here: this screen never itself changes mastery. */}
          {onTestCard && (
            <button
              onClick={() => onTestCard(cocktail)}
              className="w-full h-6 flex items-center justify-center transition-colors"
              style={{ background: getMasteryBannerColor(cardMastery[cocktail.id]) }}
              title="Practise this card"
              data-testid={`test-card-btn-full-${cocktail.id}`}
            >
              <span className="text-[10px] font-bold uppercase tracking-wider text-white">{getMasteryBannerLabel(cardMastery[cocktail.id])}</span>
            </button>
          )}
          {onTestCard && cardMastery[cocktail.id]?.mastered && daysUntil(cardMastery[cocktail.id]?.review_due_at) > 0 && (
            <p className="text-center text-[9px] uppercase tracking-wide py-1" style={{ color: C.textMuted, background: C.headerBg }} data-testid={`review-due-${cocktail.id}`}>
              Review in {daysUntil(cardMastery[cocktail.id].review_due_at)} day{daysUntil(cardMastery[cocktail.id].review_due_at) === 1 ? "" : "s"}
            </p>
          )}
          {/* Header + recipe controls */}
          <div className="flex items-center justify-between gap-2 px-4 py-2.5 flex-wrap" style={{ background: C.headerBg }}>
            <span className="hidden sm:inline text-white font-bold tracking-wide text-sm" style={{ fontFamily: "Georgia, serif" }}>FlashSpecs <span style={{ color: C.copper }}>|</span></span>
            <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
              {hasBatch && (
                <div className="flex rounded-full overflow-hidden border" style={{ borderColor: "#333" }}>
                  <button
                    onClick={() => setRecipeMode("batch")}
                    className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide whitespace-nowrap transition-all"
                    style={recipeMode === "batch" ? { background: C.copper, color: "#000" } : { color: C.textMuted }}
                    data-testid="study-recipe-mode-batch"
                  >
                    Batch Measurements
                  </button>
                  <button
                    onClick={() => setRecipeMode("full")}
                    className="px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide whitespace-nowrap transition-all"
                    style={recipeMode === "full" ? { background: C.copper, color: "#000" } : { color: C.textMuted }}
                    data-testid="study-recipe-mode-full"
                  >
                    Full Recipe & Method
                  </button>
                </div>
              )}
              <button
                onClick={() => setHideRecipe(h => { const next = !h; setRevealed(!next); return next; })}
                className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-wide border transition-all"
                style={hideRecipe ? { background: C.copper, color: "#000", borderColor: C.copper } : { color: C.textMuted, borderColor: "#333" }}
                title="Recall mode hides the recipe so you can test your memory before revealing it — this never counts as Practise or Test"
                data-testid="hide-recipe-toggle"
              >
                {hideRecipe ? <EyeOff size={11} /> : <Eye size={11} />} Recall Mode: {hideRecipe ? "On" : "Off"}
              </button>
            </div>
          </div>

          {/* Image Section: Hero left (fills a fixed, viewport-friendly height on
              mobile) + 2×2 grid right — stays prominent without pushing
              ingredients/nav off a short mobile screen */}
          <div className="flex h-48 sm:h-64 md:h-72">
            {/* Hero cocktail image */}
            <div className="flex-1 flex items-center justify-center overflow-hidden" style={{ background: "#000" }}>
              {cocktail.image_url ? (
                <img src={`${BACKEND}${cocktail.image_url}`} alt={cocktail.name} className="w-full h-full object-cover" data-testid="flashspec-hero-image" />
              ) : (
                <Wine size={40} style={{ color: "#333" }} />
              )}
            </div>
            {/* 2×2 grid - stretch rows to fill full height. Hidden while
                recall-testing since glassware/garnish/method/ice are "answers" too. */}
            {revealed && (
              <div className="grid grid-cols-2 grid-rows-2 gap-px shrink-0" style={{ width: "45%", background: C.gridLine }}>
                {badges.slice(0, 4).map((b, i) => (
                  <button key={i} onClick={() => openPopup(b.type, b.name)} className="overflow-hidden transition-all hover:brightness-125" style={{ background: C.cardBg }} data-testid={`badge-${b.type}`}>
                    {b.item?.image_url ? (
                      <img src={`${BACKEND}${b.item.image_url}`} alt={b.label} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 p-2 text-center">
                        <b.icon size={20} style={{ color: C.copper, opacity: 0.7 }} />
                        <span
                          className="text-[10px] sm:text-xs font-semibold leading-tight line-clamp-2 break-words"
                          style={{ color: "#d4c4a0", fontFamily: "Georgia, serif" }}
                          data-testid={`badge-name-${b.type}`}
                        >
                          {b.name && b.name !== "—" ? b.name : b.label}
                        </span>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
            {!revealed && (
              <div className="shrink-0 flex items-center justify-center" style={{ width: "45%", background: C.gridLine }}>
                <EyeOff size={22} style={{ color: "#555" }} />
              </div>
            )}
          </div>

          {/* Name Banner */}
          <div className="text-center py-3 px-4" style={{ background: C.banner }}>
            <h2 className="text-xl sm:text-2xl font-bold text-white uppercase tracking-wider" style={{ fontFamily: "Georgia, serif" }} data-testid="flashspec-name">{cocktail.name}</h2>
          </div>

          {/* Subtitle */}
          <div className="text-center py-2 border-b" style={{ borderColor: C.copperLight }}>
            <p className="text-sm font-semibold" style={{ color: "#d4c4a0", fontFamily: "Georgia, serif" }}>
              {cocktail.category}{cocktail.base_spirit ? `  ·  ${cocktail.base_spirit}` : ""}
            </p>
          </div>

          {revealed ? (
            <>
              {hasBatch && (
                <p className="text-center text-[11px] px-4 py-1.5 border-b" style={{ color: C.textMuted, borderColor: C.copperLight }} data-testid="batch-explainer">
                  {recipeMode === "batch"
                    ? `"${cocktail.batch_ingredient_name}" below is a single pre-batched pour. Switch to Full Recipe & Method to see the individual spirits inside it.`
                    : `Showing the individual spirits that make up "${cocktail.batch_ingredient_name}".`}
                </p>
              )}
              {batchDataIncomplete && (
                <div className="flex items-start gap-2 px-4 py-2 border-b" style={{ borderColor: "rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.08)" }} data-testid="batch-data-incomplete-flag">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" style={{ color: "#ef4444" }} />
                  <p className="text-xs" style={{ color: "#fca5a5" }}>Full recipe breakdown isn't saved for this cocktail yet — showing the batch line instead.</p>
                </div>
              )}

              {/* Ingredients — tap anywhere to show all ingredients popup */}
              <button
                onClick={() => {
                  const ingredientsList = displayIngredients.map(ing =>
                    `${ing.brand ? ing.brand + ' ' : ''}${ing.name} — ${ing.measurement}`
                  ).join('\n') || 'No ingredients listed';
                  setPopup({ name: `${cocktail.name} — Ingredients (${recipeMode === "full" ? "Full Recipe & Method" : "Batch Measurements"})`, description: ingredientsList, image_url: null, youtube_link: null });
                }}
                className="w-full text-left px-4 py-3 space-y-0 transition-all hover:bg-white/5 border-b"
                style={{ borderColor: C.copperLight }}
                data-testid="ingredients-section"
              >
                <p className="text-[10px] uppercase tracking-widest font-bold mb-1.5" style={{ color: C.copper }}>Ingredients</p>
                {displayIngredients.map((ing, idx) => {
                  const isBatchLine = recipeMode === "batch" && batchNameLower && (ing.name || "").trim().toLowerCase() === batchNameLower;
                  return (
                    <div key={idx} className="flex items-center gap-2 py-1 px-2" data-testid={`ingredient-${idx}`}>
                      <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: C.copper }} />
                      <span className="flex-1 text-sm font-medium truncate text-white">{ing.brand ? `${ing.brand} ${ing.name}` : ing.name}</span>
                      {isBatchLine && (
                        <span className="text-[8px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded shrink-0" style={{ color: "#000", background: C.copper }} data-testid={`ingredient-batched-tag-${idx}`}>Batched</span>
                      )}
                      <span className="text-xs font-bold shrink-0" style={{ color: C.copper }}>{ing.measurement}</span>
                    </div>
                  );
                })}
              </button>

              {/* Notes (allergens / batch info) */}
              {cocktail.notes && (
                <div className="px-4 py-3 border-b" style={{ borderColor: C.copperLight }}>
                  <p className="text-xs leading-relaxed text-center" style={{ color: C.textLight, fontFamily: "Georgia, serif" }}>{cocktail.notes}</p>
                </div>
              )}

              {/* Description for guests */}
              {cocktail.description && (
                <div className="px-4 py-3 border-b" style={{ borderColor: C.copperLight }}>
                  <p className="text-sm leading-relaxed" style={{ color: C.textLight, fontFamily: "Georgia, serif" }}>{cocktail.description}</p>
                </div>
              )}
            </>
          ) : (
            <div className="px-4 py-8 text-center space-y-3" data-testid="recipe-hidden-panel">
              <EyeOff size={28} className="mx-auto" style={{ color: "#555" }} />
              <p className="text-sm" style={{ color: C.textMuted }}>Recipe hidden — try to recall the glass, garnish, method and every measurement before revealing it.</p>
              <button
                onClick={() => setRevealed(true)}
                className="px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wide"
                style={{ background: C.copper, color: "#000" }}
                data-testid="reveal-recipe-btn"
              >
                Reveal Recipe
              </button>
            </div>
          )}

          {/* Nav buttons */}
          <div className="flex items-center gap-2 px-4 py-3" style={{ background: C.headerBg }}>
            <button onClick={prev} disabled={currentIndex === 0} className="flex-1 py-2 rounded-lg text-sm font-bold border-2 transition-all disabled:opacity-30" style={{ borderColor: C.copper, color: C.copper, background: "transparent" }} data-testid="flashspec-prev">
              <ChevronLeft size={16} className="inline mr-1" />Prev
            </button>
            <button onClick={next} disabled={currentIndex === cocktails.length - 1} className="flex-1 py-2 rounded-lg text-sm font-bold text-white transition-all disabled:opacity-30" style={{ background: C.copper }} data-testid="flashspec-next">
              Next<ChevronRight size={16} className="inline ml-1" />
            </button>
          </div>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-1.5 pt-1">
          {cocktails.length <= 20 ? cocktails.map((_, idx) => (
            <button key={idx} onClick={() => goToCard(idx)} aria-label={`Go to card ${idx + 1}`} className="w-2 h-2 rounded-full transition-all" style={{ background: idx === currentIndex ? C.copper : "rgba(220,170,44,0.25)" }} data-testid={`flashspec-dot-${idx}`} />
          )) : (
            <span className="text-xs" style={{ color: C.textMuted }}>{currentIndex + 1} of {cocktails.length}</span>
          )}
        </div>
      </div>

      {popup && <InfoPopup item={popup} onClose={() => setPopup(null)} />}
    </>
  );
}

/* ═══════════════════════════════════════════════════════════
   WINE DECKS VIEW — Category browser for wines
   ═══════════════════════════════════════════════════════════ */
function WineDecksView({ wines, onSelectWine, onBack }) {
  const [search, setSearch] = useState("");
  const [manualExpanded, setManualExpanded] = useState(null);
  const isSearching = search.trim().length > 0;
  const q = search.toLowerCase();

  const decks = useMemo(() => {
    const groups = {};
    wines.forEach(w => {
      // Category = "Colour - Region" (e.g., "Red - French")
      const cat = [w.colour, w.region].filter(Boolean).join(" · ") || "Uncategorized";
      if (!groups[cat]) groups[cat] = { all: [], filtered: [] };
      groups[cat].all.push(w);
      if (!isSearching || w.name.toLowerCase().includes(q) || (w.producer || "").toLowerCase().includes(q) || (w.grape || "").toLowerCase().includes(q)) {
        groups[cat].filtered.push(w);
      }
    });
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [wines, isSearching, q]);

  const totalMatches = isSearching ? decks.reduce((s, [, d]) => s + d.filtered.length, 0) : 0;

  return (
    <div className="max-w-2xl mx-auto rounded-xl border overflow-hidden shadow-2xl" style={{ borderColor: C.copper, background: C.cardBg }} data-testid="wine-decks-view">
      <div className="flex items-center justify-between px-4 py-2.5" style={{ background: C.headerBg }}>
        <span className="text-white font-bold tracking-wide text-sm" style={{ fontFamily: "Georgia, serif" }}>
          FlashSpecs <span style={{ color: C.copper }}>|</span> <span className="font-normal" style={{ color: C.textMuted }}>Wine</span>
        </span>
        <button onClick={onBack} className="rounded-full p-1 hover:bg-white/10" aria-label="Back"><X size={18} style={{ color: C.copper }} /></button>
      </div>
      <div className="px-3 py-2 border-b" style={{ borderColor: C.copperLight }}>
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border transition-shadow focus-within:ring-2 focus-within:ring-[hsl(var(--primary))]" style={{ borderColor: C.copperLight, background: "rgba(255,255,255,0.05)" }}>
          <Search size={16} style={{ color: C.textMuted }} />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search wines..." className="bg-transparent border-none outline-none text-sm flex-1 text-white placeholder:text-[#555]" data-testid="wine-flashspec-search" />
        </div>
      </div>
      <div className="p-3 space-y-2 max-h-[65vh] overflow-y-auto">
        {isSearching && totalMatches === 0 && <p className="text-center text-xs py-6" style={{ color: C.textMuted }}>No wines match &ldquo;{search}&rdquo;</p>}
        {decks.map(([category, { all, filtered }]) => {
          if (isSearching && filtered.length === 0) return null;
          const isExpanded = isSearching ? filtered.length > 0 : manualExpanded === category;
          const displayItems = isSearching ? filtered : all;
          return (
            <div key={category}>
              <button onClick={() => { if (!isSearching) setManualExpanded(isExpanded ? null : category); }} className="w-full flex items-center justify-between px-4 py-3 rounded-lg" style={{ background: C.banner }} data-testid={`wine-deck-${category}`}>
                <div className="flex items-center gap-3">
                  <span className="text-white font-bold text-sm uppercase tracking-wide" style={{ fontFamily: "Georgia, serif" }}>{category}</span>
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: "rgba(184,151,106,0.2)", color: C.copper }}>{isSearching ? `${filtered.length}/${all.length}` : all.length}</span>
                </div>
                <ChevronDown size={16} className="transition-transform" style={{ color: C.textMuted, transform: isExpanded ? "rotate(180deg)" : "none" }} />
              </button>
              {isExpanded && (
                <div className="mt-2 space-y-2 pl-1">
                  {displayItems.map(wine => (
                    <button key={wine.id} onClick={() => onSelectWine(wine)} className="w-full rounded-lg border-2 overflow-hidden transition-all hover:shadow-lg text-left" style={{ borderColor: C.copper, background: "rgba(255,255,255,0.03)" }} data-testid={`wine-item-fs-${wine.id}`}>
                      <div className="flex items-center">
                        <div className="w-16 h-16 shrink-0 flex items-center justify-center border-r" style={{ borderColor: C.copperLight, background: wine.image_url ? "transparent" : "rgba(255,255,255,0.04)" }}>
                          {wine.image_url ? <img src={`${BACKEND}${wine.image_url}`} alt={wine.name} className="w-full h-full object-cover" /> : <Grape size={22} style={{ color: "#333" }} />}
                        </div>
                        <div className="flex-1 px-3 py-2">
                          <p className="text-sm font-bold text-white uppercase tracking-wide" style={{ fontFamily: "Georgia, serif" }}>{wine.name}</p>
                          <p className="text-xs mt-0.5" style={{ color: C.textMuted }}>{[wine.producer, wine.year, wine.grape].filter(Boolean).join(" · ")}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="px-4 py-2 flex justify-end" style={{ background: C.headerBg }}>
        <button onClick={onBack} className="rounded-full p-1.5" style={{ color: C.copper }} aria-label="Close wine deck list"><X size={20} /></button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   FOOD DECKS VIEW — Category browser for food
   ═══════════════════════════════════════════════════════════ */
function FoodDecksView({ food, onSelectFood, onBack }) {
  const [search, setSearch] = useState("");
  const [manualExpanded, setManualExpanded] = useState(null);
  const isSearching = search.trim().length > 0;
  const q = search.toLowerCase();

  const decks = useMemo(() => {
    const groups = {};
    food.forEach(f => {
      const cat = f.category || "Uncategorized";
      if (!groups[cat]) groups[cat] = { all: [], filtered: [] };
      groups[cat].all.push(f);
      if (!isSearching || f.name.toLowerCase().includes(q) || (f.description || "").toLowerCase().includes(q)) {
        groups[cat].filtered.push(f);
      }
    });
    const order = ["Starters", "Mains", "Desserts", "Sides", "Snacks"];
    return Object.entries(groups).sort((a, b) => {
      const ai = order.indexOf(a[0]);
      const bi = order.indexOf(b[0]);
      if (ai >= 0 && bi >= 0) return ai - bi;
      if (ai >= 0) return -1;
      if (bi >= 0) return 1;
      return a[0].localeCompare(b[0]);
    });
  }, [food, isSearching, q]);

  const totalMatches = isSearching ? decks.reduce((s, [, d]) => s + d.filtered.length, 0) : 0;

  return (
    <div className="max-w-2xl mx-auto rounded-xl border overflow-hidden shadow-2xl" style={{ borderColor: C.copper, background: C.cardBg }} data-testid="food-decks-view">
      <div className="flex items-center justify-between px-4 py-2.5" style={{ background: C.headerBg }}>
        <span className="text-white font-bold tracking-wide text-sm" style={{ fontFamily: "Georgia, serif" }}>
          FlashSpecs <span style={{ color: C.copper }}>|</span> <span className="font-normal" style={{ color: C.textMuted }}>Food</span>
        </span>
        <button onClick={onBack} className="rounded-full p-1 hover:bg-white/10" aria-label="Back"><X size={18} style={{ color: C.copper }} /></button>
      </div>
      <div className="px-3 py-2 border-b" style={{ borderColor: C.copperLight }}>
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg border transition-shadow focus-within:ring-2 focus-within:ring-[hsl(var(--primary))]" style={{ borderColor: C.copperLight, background: "rgba(255,255,255,0.05)" }}>
          <Search size={16} style={{ color: C.textMuted }} />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search food..." className="bg-transparent border-none outline-none text-sm flex-1 text-white placeholder:text-[#555]" data-testid="food-flashspec-search" />
        </div>
      </div>
      <div className="p-3 space-y-2 max-h-[65vh] overflow-y-auto">
        {isSearching && totalMatches === 0 && <p className="text-center text-xs py-6" style={{ color: C.textMuted }}>No food items match &ldquo;{search}&rdquo;</p>}
        {decks.map(([category, { all, filtered }]) => {
          if (isSearching && filtered.length === 0) return null;
          const isExpanded = isSearching ? filtered.length > 0 : manualExpanded === category;
          const displayItems = isSearching ? filtered : all;
          return (
            <div key={category}>
              <button onClick={() => { if (!isSearching) setManualExpanded(isExpanded ? null : category); }} className="w-full flex items-center justify-between px-4 py-3 rounded-lg" style={{ background: C.banner }} data-testid={`food-deck-${category}`}>
                <div className="flex items-center gap-3">
                  <span className="text-white font-bold text-sm uppercase tracking-wide" style={{ fontFamily: "Georgia, serif" }}>{category}</span>
                  <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ background: "rgba(184,151,106,0.2)", color: C.copper }}>{isSearching ? `${filtered.length}/${all.length}` : all.length}</span>
                </div>
                <ChevronDown size={16} className="transition-transform" style={{ color: C.textMuted, transform: isExpanded ? "rotate(180deg)" : "none" }} />
              </button>
              {isExpanded && (
                <div className="mt-2 space-y-2 pl-1">
                  {displayItems.map(item => (
                    <button key={item.id} onClick={() => onSelectFood(item)} className="w-full rounded-lg border-2 overflow-hidden transition-all hover:shadow-lg text-left" style={{ borderColor: C.copper, background: "rgba(255,255,255,0.03)" }} data-testid={`food-item-fs-${item.id}`}>
                      <div className="flex items-center">
                        <div className="w-16 h-16 shrink-0 flex items-center justify-center border-r" style={{ borderColor: C.copperLight, background: item.image_url ? "transparent" : "rgba(255,255,255,0.04)" }}>
                          {item.image_url ? <img src={`${BACKEND}${item.image_url}`} alt={item.name} className="w-full h-full object-cover" /> : <UtensilsCrossed size={22} style={{ color: "#333" }} />}
                        </div>
                        <div className="flex-1 px-3 py-2">
                          <p className="text-sm font-bold text-white uppercase tracking-wide" style={{ fontFamily: "Georgia, serif" }}>{item.name}</p>
                          {item.allergens && <p className="text-xs mt-0.5" style={{ color: C.textMuted }}>{item.allergens}</p>}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="px-4 py-2 flex justify-end" style={{ background: C.headerBg }}>
        <button onClick={onBack} className="rounded-full p-1.5" style={{ color: C.copper }} aria-label="Close food deck list"><X size={20} /></button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   WINE CARD VIEW
   ═══════════════════════════════════════════════════════════ */
function WineCardView({ wines, startIndex, onBack }) {
  const [currentIndex, setCurrentIndex] = useState(startIndex || 0);
  const prev = () => setCurrentIndex(i => Math.max(0, i - 1));
  const next = () => setCurrentIndex(i => Math.min(wines.length - 1, i + 1));
  const wine = wines[currentIndex];
  if (!wine) return null;

  return (
    <div className="max-w-2xl mx-auto space-y-3">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1 text-sm font-medium" style={{ color: C.textMuted }} data-testid="wine-card-back"><ArrowLeft size={16} /> Back</button>
        <span className="text-xs" style={{ color: C.textMuted }}>{currentIndex + 1} / {wines.length}</span>
      </div>
      <div className="rounded-xl overflow-hidden border shadow-2xl" style={{ borderColor: C.copper, background: C.cardBg }}>
        <div className="flex items-center justify-between px-4 py-2.5" style={{ background: C.headerBg }}>
          <span className="text-white font-bold tracking-wide text-sm" style={{ fontFamily: "Georgia, serif" }}>FlashSpecs <span style={{ color: C.copper }}>|</span> <span className="font-normal" style={{ color: C.textMuted }}>Wine</span></span>
        </div>
        {/* Wine image */}
        <div className="w-full aspect-[16/9] flex items-center justify-center border-b" style={{ borderColor: C.copper, background: "rgba(255,255,255,0.04)" }}>
          {wine.image_url ? <img src={`${BACKEND}${wine.image_url}`} alt={wine.name} className="w-full h-full object-cover" /> : <Grape size={50} style={{ color: "#333" }} />}
        </div>
        {/* Name */}
        <div className="text-center py-3 px-4" style={{ background: C.banner }}>
          <h2 className="text-xl sm:text-2xl font-bold text-white uppercase tracking-wider" style={{ fontFamily: "Georgia, serif" }}>{wine.name}</h2>
        </div>
        {/* Details */}
        <div className="text-center py-2 border-b" style={{ borderColor: C.copperLight }}>
          <p className="text-sm font-semibold" style={{ color: "#d4c4a0", fontFamily: "Georgia, serif" }}>{[wine.producer, wine.region, wine.year].filter(Boolean).join("  ·  ")}</p>
        </div>
        {/* Info grid */}
        <div className="grid grid-cols-2 gap-0 border-b" style={{ borderColor: C.copperLight }}>
          {[{ label: "Grape", value: wine.grape }, { label: "Colour", value: wine.colour }, { label: "Glass", value: wine.glass }, { label: "Year", value: wine.year }].filter(f => f.value).map((f, i) => (
            <div key={i} className="px-4 py-2 text-center" style={{ borderRight: i % 2 === 0 ? `1px solid ${C.copperLight}` : "none", borderBottom: i < 2 ? `1px solid ${C.copperLight}` : "none" }}>
              <p className="text-[9px] uppercase tracking-widest font-bold" style={{ color: C.copper }}>{f.label}</p>
              <p className="text-sm text-white font-medium mt-0.5">{f.value}</p>
            </div>
          ))}
        </div>
        {/* Description */}
        {wine.description && (
          <div className="px-4 py-3 border-b" style={{ borderColor: C.copperLight }}>
            <p className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: C.copper }}>How to describe</p>
            <p className="text-sm leading-relaxed" style={{ color: C.textLight, fontFamily: "Georgia, serif" }}>{wine.description}</p>
          </div>
        )}
        {/* Tasting Notes */}
        {wine.tasting_notes && (
          <div className="px-4 py-3 border-b" style={{ borderColor: C.copperLight }}>
            <p className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: C.copper }}>Tasting Notes</p>
            <p className="text-sm leading-relaxed" style={{ color: C.textLight, fontFamily: "Georgia, serif" }}>{wine.tasting_notes}</p>
          </div>
        )}
        {/* Food Pairings */}
        {wine.food_pairings && (
          <div className="px-4 py-3 border-b" style={{ borderColor: C.copperLight }}>
            <p className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: C.copper }}>Food Pairings</p>
            <p className="text-sm leading-relaxed" style={{ color: C.textLight, fontFamily: "Georgia, serif" }}>{wine.food_pairings}</p>
          </div>
        )}
        {/* Nav */}
        <div className="flex items-center gap-2 px-4 py-3" style={{ background: C.headerBg }}>
          <button onClick={prev} disabled={currentIndex === 0} className="flex-1 py-2 rounded-lg text-sm font-bold border-2 disabled:opacity-30" style={{ borderColor: C.copper, color: C.copper, background: "transparent" }} data-testid="wine-card-prev"><ChevronLeft size={16} className="inline mr-1" />Prev</button>
          <button onClick={next} disabled={currentIndex === wines.length - 1} className="flex-1 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-30" style={{ background: C.copper }} data-testid="wine-card-next">Next<ChevronRight size={16} className="inline ml-1" /></button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   FOOD CARD VIEW
   ═══════════════════════════════════════════════════════════ */
function FoodCardView({ food, startIndex, onBack }) {
  const [currentIndex, setCurrentIndex] = useState(startIndex || 0);
  const prev = () => setCurrentIndex(i => Math.max(0, i - 1));
  const next = () => setCurrentIndex(i => Math.min(food.length - 1, i + 1));
  const item = food[currentIndex];
  if (!item) return null;

  return (
    <div className="max-w-2xl mx-auto space-y-3">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-1 text-sm font-medium" style={{ color: C.textMuted }} data-testid="food-card-back"><ArrowLeft size={16} /> Back</button>
        <span className="text-xs" style={{ color: C.textMuted }}>{currentIndex + 1} / {food.length}</span>
      </div>
      <div className="rounded-xl overflow-hidden border shadow-2xl" style={{ borderColor: C.copper, background: C.cardBg }}>
        <div className="flex items-center justify-between px-4 py-2.5" style={{ background: C.headerBg }}>
          <span className="text-white font-bold tracking-wide text-sm" style={{ fontFamily: "Georgia, serif" }}>FlashSpecs <span style={{ color: C.copper }}>|</span> <span className="font-normal" style={{ color: C.textMuted }}>Food</span></span>
        </div>
        {/* Food image */}
        <div className="w-full aspect-[16/9] flex items-center justify-center border-b" style={{ borderColor: C.copper, background: "rgba(255,255,255,0.04)" }}>
          {item.image_url ? <img src={`${BACKEND}${item.image_url}`} alt={item.name} className="w-full h-full object-cover" /> : <UtensilsCrossed size={50} style={{ color: "#333" }} />}
        </div>
        {/* Name */}
        <div className="text-center py-3 px-4" style={{ background: C.banner }}>
          <h2 className="text-xl sm:text-2xl font-bold text-white uppercase tracking-wider" style={{ fontFamily: "Georgia, serif" }}>{item.name}</h2>
        </div>
        {/* Course */}
        <div className="text-center py-2 border-b" style={{ borderColor: C.copperLight }}>
          <p className="text-sm font-semibold" style={{ color: "#d4c4a0", fontFamily: "Georgia, serif" }}>{item.category || "Menu Item"}</p>
        </div>
        {/* Description */}
        {item.description && (
          <div className="px-4 py-3 border-b" style={{ borderColor: C.copperLight }}>
            <p className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: C.copper }}>Description</p>
            <p className="text-sm leading-relaxed" style={{ color: C.textLight, fontFamily: "Georgia, serif" }}>{item.description}</p>
          </div>
        )}
        {/* Guest Description */}
        {item.guest_description && (
          <div className="px-4 py-3 border-b" style={{ borderColor: C.copperLight }}>
            <p className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: C.copper }}>How to describe</p>
            <p className="text-sm leading-relaxed" style={{ color: C.textLight, fontFamily: "Georgia, serif" }}>{item.guest_description}</p>
          </div>
        )}
        {/* Key Ingredients */}
        {item.key_ingredients && (
          <div className="px-4 py-3 border-b" style={{ borderColor: C.copperLight }}>
            <p className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: C.copper }}>Key Ingredients</p>
            <p className="text-sm leading-relaxed" style={{ color: C.textLight }}>{item.key_ingredients}</p>
          </div>
        )}
        {/* Allergens */}
        {item.allergens && (
          <div className="px-4 py-3 border-b" style={{ borderColor: C.copperLight }}>
            <p className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: "#c44" }}>Allergens</p>
            <p className="text-sm leading-relaxed text-white font-medium">{item.allergens}</p>
          </div>
        )}
        {/* Suggested Pairings */}
        {item.suggested_pairings && (
          <div className="px-4 py-3 border-b" style={{ borderColor: C.copperLight }}>
            <p className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: C.copper }}>Suggested Pairings</p>
            <p className="text-sm leading-relaxed" style={{ color: C.textLight, fontFamily: "Georgia, serif" }}>{item.suggested_pairings}</p>
          </div>
        )}
        {/* Nav */}
        <div className="flex items-center gap-2 px-4 py-3" style={{ background: C.headerBg }}>
          <button onClick={prev} disabled={currentIndex === 0} className="flex-1 py-2 rounded-lg text-sm font-bold border-2 disabled:opacity-30" style={{ borderColor: C.copper, color: C.copper, background: "transparent" }} data-testid="food-card-prev"><ChevronLeft size={16} className="inline mr-1" />Prev</button>
          <button onClick={next} disabled={currentIndex === food.length - 1} className="flex-1 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-30" style={{ background: C.copper }} data-testid="food-card-next">Next<ChevronRight size={16} className="inline ml-1" /></button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MAIN PAGE COMPONENT
   ═══════════════════════════════════════════════════════════ */
export default function FlashSpecsPage() {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [cocktails, setCocktails] = useState([]);
  const [wines, setWines] = useState([]);
  const [food, setFood] = useState([]);
  const [library, setLibrary] = useState([]);
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testName, setTestName] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [hiddenCategories, setHiddenCategories] = useState([]);
  const [deckOrder, setDeckOrder] = useState([]);

  // Views: 'home' | 'decks' | 'card' | 'wine-decks' | 'wine-card' | 'food-decks' | 'food-card'
  const [view, setView] = useState(testId ? "card" : "home");
  const [selectedCocktails, setSelectedCocktails] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedRecipeMode, setSelectedRecipeMode] = useState("batch");
  const [selectedWines, setSelectedWines] = useState([]);
  const [selectedWineIndex, setSelectedWineIndex] = useState(0);
  const [selectedFood, setSelectedFood] = useState([]);
  const [selectedFoodIndex, setSelectedFoodIndex] = useState(0);
  const [cardMastery, setCardMastery] = useState({});
  const [testingCocktail, setTestingCocktail] = useState(null);
  const [ingredientNames, setIngredientNames] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const promises = [
          testId ? axios.get(`${API}/saved-tests/${testId}/flashcards`) : axios.get(`${API}/cocktails`),
          axios.get(`${API}/library`),
          axios.get(`${API}/wines`),
          axios.get(`${API}/food`),
          axios.get(`${API}/decks`),
          axios.get(`${API}/auth/me`).catch(() => ({ data: null })),
          axios.get(`${API}/org/hidden-categories`).catch(() => ({ data: { hidden_categories: [] } })),
          axios.get(`${API}/org/deck-order`).catch(() => ({ data: { deck_order: [] } })),
          axios.get(`${API}/cocktails-meta/filters`).catch(() => ({ data: { ingredient_names: [] } })),
        ];
        const [flashRes, libRes, wineRes, foodRes, decksRes, userRes, hiddenRes, orderRes, metaRes] = await Promise.all(promises);
        const data = testId ? flashRes.data.cocktails : flashRes.data;
        setCocktails(data);
        setLibrary(libRes.data);
        setWines(wineRes.data);
        setFood(foodRes.data);
        setDecks(decksRes.data);
        setCurrentUser(userRes.data);
        setHiddenCategories(hiddenRes.data?.hidden_categories || []);
        setDeckOrder(orderRes.data?.deck_order || []);
        setIngredientNames(metaRes.data?.ingredient_names || []);
        if (testId) {
          setTestName(flashRes.data.test_name || "");
          setSelectedCocktails(data);
        }
      } catch {
        /* non-fatal: fall back to whatever data was already loaded */
      }
      setLoading(false);
    };
    load();
  }, [testId]);

  // Auto-jump into a deck+card when the URL carries ?deck=X&card=Y (used by
  // the dashboard's "Continue Studying" tiles).
  useEffect(() => {
    if (loading || testId || !cocktails.length) return;
    const params = new URLSearchParams(window.location.search);
    const wantDeck = params.get("deck");
    const wantCard = params.get("card");
    if (!wantDeck) return;
    const peers = cocktails.filter(c => (c.category || "Uncategorized") === wantDeck);
    if (!peers.length) return;
    const idx = wantCard ? peers.findIndex(c => c.id === wantCard) : 0;
    setSelectedCocktails(peers);
    setSelectedIndex(idx >= 0 ? idx : 0);
    setView("card");
    // Clean the params so a manual back-nav doesn't re-trigger the jump
    window.history.replaceState({}, "", window.location.pathname);
  }, [loading, testId, cocktails]);

  const handleSelectCocktail = (cocktail, recipeMode = "batch") => {
    const category = cocktail.category || "Uncategorized";
    const peers = cocktails.filter(c => c.category === category);
    const idx = peers.findIndex(c => c.id === cocktail.id);
    setSelectedCocktails(peers);
    setSelectedIndex(idx >= 0 ? idx : 0);
    setSelectedRecipeMode(recipeMode);
    setView("card");
  };

  const handleSelectDeckCocktail = (cocktail, deckCocktails, recipeMode = "batch") => {
    const idx = deckCocktails.findIndex(c => c.id === cocktail.id);
    setSelectedCocktails(deckCocktails);
    setSelectedIndex(idx >= 0 ? idx : 0);
    setSelectedRecipeMode(recipeMode);
    setView("card");
  };

  // Load "test this card" mastery/glow state for every cocktail currently loaded
  useEffect(() => {
    if (!currentUser || !cocktails.length) return;
    const ids = cocktails.map(c => c.id).join(",");
    axios.get(`${API}/card-mastery`, { params: { cocktail_ids: ids } })
      .then(res => setCardMastery(res.data))
      .catch(() => {});
  }, [currentUser, cocktails]);

  const handleTestCard = (cocktail) => setTestingCocktail(cocktail);
  const handleCardTestComplete = (cocktailId, masteryEntry) => {
    setCardMastery(prev => ({ ...prev, [cocktailId]: masteryEntry }));
  };

  // Beacon: track which cocktail deck the user is browsing so the dashboard
  // "Continue Studying" tiles can resume from the right card.
  useEffect(() => {
    if (view !== "card") return;
    const current = selectedCocktails[selectedIndex];
    if (!current) return;
    const deck = current.category || "Uncategorized";
    axios.post(`${API}/dashboard/deck-view`, { deck_name: deck, card_id: current.id }).catch(() => {});
  }, [view, selectedIndex, selectedCocktails]);

  const handleSelectWine = (wine) => {
    const cat = [wine.colour, wine.region].filter(Boolean).join(" · ") || "Uncategorized";
    const peers = wines.filter(w => ([w.colour, w.region].filter(Boolean).join(" · ") || "Uncategorized") === cat);
    const idx = peers.findIndex(w => w.id === wine.id);
    setSelectedWines(peers);
    setSelectedWineIndex(idx >= 0 ? idx : 0);
    setView("wine-card");
  };

  const handleSelectFood = (item) => {
    const cat = item.category || "Uncategorized";
    const peers = food.filter(f => (f.category || "Uncategorized") === cat);
    const idx = peers.findIndex(f => f.id === item.id);
    setSelectedFood(peers);
    setSelectedFoodIndex(idx >= 0 ? idx : 0);
    setView("food-card");
  };

  const handleBackFromCard = () => {
    if (testId) {
      navigate(-1);
    } else {
      setView("decks");
    }
  };

  const handleChallenge = (cocktailIds, deckName, recipeMode = "batch") => {
    navigate("/flashspecs/challenge", { state: { cocktailIds, deckName, recipeMode } });
  };

  const handleTest = async (cocktailIds, deckName, recipeMode = "batch") => {
    try {
      const res = await axios.post(`${API}/deck-test/start`, {
        cocktail_ids: cocktailIds,
        deck_name: deckName,
        recipe_mode: recipeMode,
      });
      navigate(`/take-test/${res.data.access_code}`);
    } catch (err) {
      console.error("Failed to start deck test:", err);
    }
  };

  const isAdmin = ["admin", "org_admin", "system_admin"].includes(currentUser?.role);

  const handleToggleHideCategory = async (category) => {
    if (!isAdmin) return;
    const next = hiddenCategories.includes(category)
      ? hiddenCategories.filter(c => c !== category)
      : [...hiddenCategories, category];
    // Optimistic update
    setHiddenCategories(next);
    try {
      const res = await axios.put(`${API}/org/hidden-categories`, { hidden_categories: next });
      setHiddenCategories(res.data?.hidden_categories || next);
    } catch (err) {
      console.error("Failed to update hidden categories:", err);
      // Rollback
      setHiddenCategories(hiddenCategories);
    }
  };

  const handleReorderDecks = async (nextOrder) => {
    if (!isAdmin) return;
    const prev = deckOrder;
    setDeckOrder(nextOrder);
    try {
      const res = await axios.put(`${API}/org/deck-order`, { deck_order: nextOrder });
      setDeckOrder(res.data?.deck_order || nextOrder);
    } catch (err) {
      console.error("Failed to save deck order:", err);
      setDeckOrder(prev);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-24"><p style={{ color: C.textMuted }}>Loading FlashSpecs...</p></div>;

  // Hero images for the FlashSpecs home cards — static decorative photos that
  // never change based on user uploads (those belong on individual flashcards).
  // Kept around purely for legacy references; the JSX no longer reads these.

  return (
    <div className={`${testId ? 'min-h-screen' : ''} px-1 py-3 md:p-6 pb-24`} style={{ background: testId ? C.pageBg : "transparent" }}>

      {/* ═══ HOME VIEW ═══ */}
      {view === "home" && (
        <div className="max-w-2xl mx-auto space-y-6" data-testid="flashspec-home">
          <div className="text-center pt-4">
            <h1 className="text-3xl font-bold text-white uppercase tracking-widest" style={{ fontFamily: "Georgia, serif" }}>FlashSpecs</h1>
            <p className="text-xs mt-2 tracking-wider" style={{ color: C.textMuted }}>Tap to explore specs</p>
          </div>

          {/* Hero card — tap to enter */}
          <button
            onClick={() => setView("decks")}
            className="w-full rounded-xl border overflow-hidden shadow-2xl transition-all hover:scale-[1.02] active:scale-[0.98] group"
            style={{ borderColor: C.copper, background: C.cardBg }}
            data-testid="flashspec-hero-enter"
          >
            <div className="relative aspect-[4/3] overflow-hidden" style={{ background: C.banner }}>
              <img
                src="https://images.unsplash.com/photo-1551024709-8f23befc6f87?w=600&q=80"
                alt="Cocktails"
                className="w-full h-full object-cover opacity-50"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 text-center">
                <p className="text-white text-2xl font-bold uppercase tracking-wider" style={{ fontFamily: "Georgia, serif" }}>
                  Cocktails
                </p>
                <p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.6)" }}>
                  {cocktails.length} Cocktails · Tap to browse
                </p>
              </div>
            </div>
            <div className="py-3 text-center" style={{ background: C.headerBg }}>
              <span className="text-sm font-bold uppercase tracking-widest" style={{ color: C.copper }}>
                Open Decks
              </span>
            </div>
          </button>

          {/* Wine card — Coming Soon */}
          <div
            className="w-full rounded-xl border overflow-hidden shadow-2xl opacity-70"
            style={{ borderColor: C.copper, background: C.cardBg }}
            data-testid="flashspec-wine-card"
          >
            <div className="relative aspect-[4/3] overflow-hidden" style={{ background: C.banner }}>
              <img src="https://images.unsplash.com/photo-1602800268530-dfd37e3eb03a?w=600&q=80" alt="Wine" className="w-full h-full object-cover opacity-50" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 text-center">
                <p className="text-white text-2xl font-bold uppercase tracking-wider" style={{ fontFamily: "Georgia, serif" }}>Wine</p>
                <p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.6)" }}>Coming Soon</p>
              </div>
            </div>
            <div className="py-3 text-center" style={{ background: C.headerBg }}>
              <span className="text-sm font-bold uppercase tracking-widest" style={{ color: "rgba(220,170,44,0.75)" }}>Coming Soon</span>
            </div>
          </div>

          {/* Food card — Coming Soon */}
          <div
            className="w-full rounded-xl border overflow-hidden shadow-2xl opacity-70"
            style={{ borderColor: C.copper, background: C.cardBg }}
            data-testid="flashspec-food-card"
          >
            <div className="relative aspect-[4/3] overflow-hidden" style={{ background: C.banner }}>
              <img src="https://images.unsplash.com/photo-1663530761401-15eefb544889?w=600&q=80" alt="Food" className="w-full h-full object-cover opacity-50" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 text-center">
                <p className="text-white text-2xl font-bold uppercase tracking-wider" style={{ fontFamily: "Georgia, serif" }}>Food</p>
                <p className="text-xs mt-1" style={{ color: "rgba(255,255,255,0.6)" }}>Coming Soon</p>
              </div>
            </div>
            <div className="py-3 text-center" style={{ background: C.headerBg }}>
              <span className="text-sm font-bold uppercase tracking-widest" style={{ color: "rgba(220,170,44,0.75)" }}>Coming Soon</span>
            </div>
          </div>
        </div>
      )}

      {/* ═══ DECKS VIEW ═══ */}
      {view === "decks" && (
        <div className="max-w-2xl mx-auto">
          <DecksView
            cocktails={cocktails}
            library={library}
            customDecks={decks}
            onSelectCocktail={handleSelectCocktail}
            onSelectDeckCocktail={handleSelectDeckCocktail}
            onChallenge={handleChallenge}
            onTest={handleTest}
            onTestCard={currentUser ? handleTestCard : undefined}
            cardMastery={cardMastery}
            onBack={() => setView("home")}
            isAdmin={isAdmin}
            hiddenCategories={hiddenCategories}
            onToggleHideCategory={handleToggleHideCategory}
            deckOrder={deckOrder}
            onReorderDecks={handleReorderDecks}
            recipeMode={selectedRecipeMode}
            onRecipeModeChange={setSelectedRecipeMode}
            ingredientNames={ingredientNames}
          />
        </div>
      )}

      {/* ═══ CARD VIEW ═══ */}
      {view === "card" && (
        <CardView
          cocktails={selectedCocktails.length > 0 ? selectedCocktails : cocktails}
          library={library}
          startIndex={selectedIndex}
          onBack={handleBackFromCard}
          testName={testName}
          onTestCard={currentUser ? handleTestCard : undefined}
          cardMastery={cardMastery}
          initialRecipeMode={selectedRecipeMode}
        />
      )}

      {/* ═══ WINE DECKS VIEW ═══ */}
      {view === "wine-decks" && (
        <div className="max-w-2xl mx-auto">
          <WineDecksView wines={wines} onSelectWine={handleSelectWine} onBack={() => setView("home")} />
        </div>
      )}

      {/* ═══ WINE CARD VIEW ═══ */}
      {view === "wine-card" && (
        <WineCardView wines={selectedWines} startIndex={selectedWineIndex} onBack={() => setView("wine-decks")} />
      )}

      {/* ═══ FOOD DECKS VIEW ═══ */}
      {view === "food-decks" && (
        <div className="max-w-2xl mx-auto">
          <FoodDecksView food={food} onSelectFood={handleSelectFood} onBack={() => setView("home")} />
        </div>
      )}

      {/* ═══ FOOD CARD VIEW ═══ */}
      {view === "food-card" && (
        <FoodCardView food={selectedFood} startIndex={selectedFoodIndex} onBack={() => setView("food-decks")} />
      )}

      {testingCocktail && (
        <CardTestModal
          cocktail={testingCocktail}
          onClose={() => setTestingCocktail(null)}
          onComplete={handleCardTestComplete}
        />
      )}
    </div>
  );
}
